import { useEffect, useRef, useState } from 'react';
import { Camera, ScanLine, ShieldCheck, CheckCircle2, LoaderCircle, ImageUp } from 'lucide-react';
import { parseArgentineDni } from '@/utils/argentineDni.js';
import { readDniImage, startDniCamera } from '@/utils/dniCamera.js';
import './dni-capture.css';

const SAVED_CAMERA = 'sinamon.dniCapture.camera';
const loadCamera = () => { try { return localStorage.getItem(SAVED_CAMERA) || ''; } catch { return ''; } };
const saveCamera = id => { try { localStorage.setItem(SAVED_CAMERA, id); } catch { /* per-viewer convenience only */ } };

/** Raw scanner text and video frames never leave capture. */
export default function DniCaptureField({ onParsed, onAttempt, disabled = false }) {
  const input = useRef(null), video = useRef(null);
  const callbacks = useRef({ onParsed, onAttempt });
  callbacks.current = { onParsed, onAttempt };
  const [camera, setCamera] = useState(false), [ready, setReady] = useState(false);
  const [message, setMessage] = useState(''), [success, setSuccess] = useState(false);
  const [devices, setDevices] = useState([]), [deviceId, setDeviceId] = useState(loadCamera);
  useEffect(() => {
    const field = input.current;
    const timer = setTimeout(() => field?.focus(), 0);
    return () => { clearTimeout(timer); if (field) field.value = ''; };
  }, []);
  const accept = (raw, fromCamera = false) => {
    const result = parseArgentineDni(raw);
    if (!result.ok) {
      // The camera keeps scanning, but a decoded unreadable code must not fail silently.
      if (!fromCamera) callbacks.current.onAttempt?.();
      setSuccess(false); setMessage(result.error.message);
      return false;
    }
    callbacks.current.onAttempt?.();
    setSuccess(true); setMessage('DNI leído. Revise los datos antes de confirmar. No verifica identidad.');
    setCamera(false); callbacks.current.onParsed(result.data);
    return true;
  };
  const acceptRef = useRef(accept); acceptRef.current = accept;
  useEffect(() => {
    if (disabled) { setCamera(false); return; }
    if (!camera) return;
    setReady(false);
    const stop = startDniCamera({ video: video.current, deviceId, onRead: raw => acceptRef.current(raw, true),
      onDevices: setDevices, onReady: () => setReady(true),
      onError: text => { setMessage(text); setSuccess(false); setCamera(false); } });
    const hide = () => { if (document.hidden) { stop(); setCamera(false); } };
    const leave = () => { stop(); setCamera(false); };
    document.addEventListener('visibilitychange', hide); window.addEventListener('pagehide', leave);
    return () => { stop(); document.removeEventListener('visibilitychange', hide); window.removeEventListener('pagehide', leave); };
  }, [camera, disabled, deviceId]);
  const readPhoto = async event => {
    const file = event.target.files?.[0]; event.target.value = '';
    if (!file || disabled) return;
    setCamera(false); setSuccess(false); setMessage('');
    try {
      const raw = await readDniImage(file);
      if (raw) accept(raw);
      else { callbacks.current.onAttempt?.(); setMessage('No se encontró el código del DNI en la foto. Pruebe con una imagen más nítida del dorso.'); }
    } catch { setMessage('No se pudo leer la foto. Intente nuevamente o continúe manualmente.'); }
  };
  const process = () => {
    if (!input.current || disabled) return;
    const raw = input.current.value; input.current.value = '';
    setCamera(false); accept(raw);
  };
  return <section className="dni-capture" aria-label="Captura de DNI">
    <div className="dni-capture-heading"><span className="dni-capture-icon"><ScanLine size={22} /></span><div><h3>Complete con su DNI</h3><p>Cámara o lectora · sin cargar datos a mano</p></div></div>
    <button type="button" className="dni-camera-button" disabled={disabled} onClick={() => {
      setMessage(''); setSuccess(false); setCamera(value => !value);
    }}><Camera size={18} />{camera ? 'Detener cámara' : 'Escanear con cámara'}</button>
    {camera && !disabled ? <div className="dni-camera-view">
      <video ref={video} muted playsInline autoPlay aria-label="Vista previa de cámara" />
      <div className="dni-camera-guide" aria-hidden="true"><i /><i /><i /><i /></div>
      <span className="dni-camera-state">{ready ? <><span /> Buscando código PDF417</> : <><LoaderCircle size={14} className="animate-spin" /> Iniciando cámara…</>}</span>
    </div> : <div className={`dni-capture-placeholder ${success ? 'is-success' : ''}`}>
      {success ? <CheckCircle2 size={34} /> : <ScanLine size={38} />}
      <strong>{success ? 'Lectura completada' : 'Acerque el código a la cámara'}</strong>
      <span>{success ? 'Los datos están listos para revisar' : 'Buena luz, sin reflejos y con el DNI quieto'}</span>
    </div>}
    {camera && <p className="dni-camera-hint">Muestre el código PDF417 completo. Acerque o aleje el DNI hasta que se vea nítido.</p>}
    {devices.length > 1 && <label className="dni-hid-label">Cámara<select value={deviceId} disabled={disabled} onChange={event => {
      saveCamera(event.target.value); setDeviceId(event.target.value);
    }}><option value="">Predeterminada</option>{devices.map((device, index) => <option key={device.deviceId} value={device.deviceId}>{device.label || `Cámara ${index + 1}`}</option>)}</select></label>}
    <label className="dni-camera-button dni-photo-button"><ImageUp size={18} />Subir foto del dorso
      <input type="file" accept="image/*" className="sr-only" disabled={disabled} onChange={readPhoto} /></label>
    <div className="dni-hid-divider"><span>O use su lectora de barras</span></div>
    <label className="dni-hid-label">Lectura del DNI<input ref={input} autoComplete="off" disabled={disabled} placeholder="Escanee aquí con la lectora" onKeyDown={event => {
      if (event.key !== 'Enter') return;
      event.preventDefault(); event.stopPropagation(); process();
    }} /></label>
    <button type="button" onClick={process} disabled={disabled} className="dni-hid-button">Procesar lectura</button>
    {message && <p role="status" className={`dni-capture-message ${success ? 'is-success' : ''}`}>{message}</p>}
    <p className="dni-capture-privacy"><ShieldCheck size={15} /> Lectura local. No se guardan fotos ni video.</p>
  </section>;
}
